import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns formatted nutrition data of the provided product.
 */
export const useProductNutritionData = (params: Params) => {
  const { baseProduct, useFallbackValues } = params;

  const { formatMessage, formatNumber } = useIntl();

  // ─── Computed Data ───────────────────────────────────────────────────

  const fallbackValue = useFallbackValues ? 0 : null;

  // ─── Messages ────────────────────────────────────────────────────────

  const totalCarbsLabel = formatMessage(messages.totalCarbs);
  const totalCarbsShortLabel = formatMessage(messages.totalCarbsShort);
  const proteinLabel = formatMessage(messages.protein);
  const caloriesLabel = formatMessage(messages.calories);
  const caloriesShortLabel = formatMessage(messages.caloriesShort);
  const sodiumLabel = formatMessage(messages.sodium);
  const transFatLabel = formatMessage(messages.transFat);
  const cholesterolLabel = formatMessage(messages.cholesterol);
  const dietaryFiberLabel = formatMessage(messages.dietaryFiber);
  const caloriesFromFatLabel = formatMessage(messages.caloriesFromFat);
  const saturatedFatLabel = formatMessage(messages.saturatedFat);
  const sugarsLabel = formatMessage(messages.sugars);
  const totalFatLabel = formatMessage(messages.totalFat);
  const totalFatShortLabel = formatMessage(messages.totalFatShort);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const formatGram = useCallback(
    (value: number) => {
      const formattedValue = formatNumber(value);

      return formatMessage(messages.gramValue, { value: formattedValue });
    },
    [formatMessage, formatNumber],
  );

  const formatMilligram = useCallback(
    (value: number) => {
      const formattedValue = formatNumber(value);

      return formatMessage(messages.milligramValue, { value: formattedValue });
    },
    [formatMessage, formatNumber],
  );

  const formatCalories = useCallback(
    (value: number) => {
      const formattedValue = formatNumber(value);

      return formatMessage(messages.caloriesValue, { value: formattedValue });
    },
    [formatMessage, formatNumber],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useMemo<NutritionDataRecord>(() => {
    const calories = baseProduct?.calories ?? fallbackValue;
    const proteinG = baseProduct?.proteinG ?? fallbackValue;
    const saturatedFatG = baseProduct?.saturatedFatG ?? fallbackValue;
    const sodiumMg = baseProduct?.sodiumMg ?? fallbackValue;
    const sugarsG = baseProduct?.sugarsG ?? fallbackValue;
    const totalCarbsG = baseProduct?.totalCarbsG ?? fallbackValue;
    const totalFatG = baseProduct?.totalFatG ?? fallbackValue;
    const transFatG = baseProduct?.transFatG ?? fallbackValue;
    const cholesterolMg = baseProduct?.cholesterolMg ?? fallbackValue;
    const caloriesFromFat = baseProduct?.caloriesFromFat ?? fallbackValue;
    const dietaryFiberG = baseProduct?.dietaryFiberG ?? fallbackValue;

    return {
      calories: checkIfValidValue(calories)
        ? { label: caloriesLabel, labelShort: caloriesShortLabel, value: formatNumber(calories) } // prettier-ignore
        : null,
      caloriesFromFat: checkIfValidValue(caloriesFromFat)
        ? { label: caloriesFromFatLabel, value: formatCalories(caloriesFromFat) } // prettier-ignore
        : null,
      saturatedFatG: checkIfValidValue(saturatedFatG)
        ? { label: saturatedFatLabel, value: formatGram(saturatedFatG) }
        : null,
      transFatG: checkIfValidValue(transFatG)
        ? { label: transFatLabel, value: formatGram(transFatG) }
        : null,
      cholesterolMg: checkIfValidValue(cholesterolMg)
        ? { label: cholesterolLabel, value: formatMilligram(cholesterolMg) }
        : null,
      sodiumMg: checkIfValidValue(sodiumMg)
        ? { label: sodiumLabel, value: formatMilligram(sodiumMg) }
        : null,
      totalCarbsG: checkIfValidValue(totalCarbsG)
        ? {
            label: totalCarbsLabel,
            labelShort: totalCarbsShortLabel,
            value: formatGram(totalCarbsG),
          } // prettier-ignore
        : null,
      dietaryFiberG: checkIfValidValue(dietaryFiberG)
        ? { label: dietaryFiberLabel, value: formatGram(dietaryFiberG) }
        : null,
      sugarsG: checkIfValidValue(sugarsG)
        ? { label: sugarsLabel, value: formatGram(sugarsG) }
        : null,
      proteinG: checkIfValidValue(proteinG)
        ? { label: proteinLabel, value: formatGram(proteinG) }
        : null,
      totalFatG: checkIfValidValue(totalFatG)
        ? { label: totalFatLabel, labelShort: totalFatShortLabel, value: formatGram(totalFatG) } // prettier-ignore
        : null,
    };
  }, [
    baseProduct?.calories,
    baseProduct?.proteinG,
    baseProduct?.saturatedFatG,
    baseProduct?.sodiumMg,
    baseProduct?.sugarsG,
    baseProduct?.totalCarbsG,
    baseProduct?.totalFatG,
    baseProduct?.transFatG,
    baseProduct?.cholesterolMg,
    baseProduct?.caloriesFromFat,
    baseProduct?.dietaryFiberG,
    fallbackValue,
    caloriesLabel,
    caloriesShortLabel,
    formatNumber,
    caloriesFromFatLabel,
    formatCalories,
    saturatedFatLabel,
    formatGram,
    transFatLabel,
    cholesterolLabel,
    formatMilligram,
    sodiumLabel,
    totalCarbsLabel,
    totalCarbsShortLabel,
    dietaryFiberLabel,
    sugarsLabel,
    proteinLabel,
    totalFatLabel,
    totalFatShortLabel,
  ]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfValidValue(value: unknown): value is number {
  return typeof value === 'number' && !Number.isNaN(value);
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  carbs: {
    defaultMessage: 'Carbs',
    description: 'Nutrition | Carbs | Label',
  },
  totalCarbs: {
    defaultMessage: 'Total Carbohydrates',
    description: 'Nutrition | Total Carbs | Label',
  },
  totalCarbsShort: {
    defaultMessage: 'Carbs',
    description: 'Nutrition | Total Carbs | Label (short)',
  },
  protein: {
    defaultMessage: 'Protein',
    description: 'Nutrition | Protein | Label',
  },
  fat: {
    defaultMessage: 'Fat',
    description: 'Nutrition | Fat | Label',
  },
  calories: {
    defaultMessage: 'Calories',
    description: 'Nutrition | Calories | Label',
  },
  caloriesShort: {
    defaultMessage: 'Cal',
    description: 'Nutrition | Calories | Label (short)',
  },
  caloriesFromFat: {
    defaultMessage: 'Calories from Fat',
    description: 'Nutrition | Calories from Fat | Label',
  },
  sodium: {
    defaultMessage: 'Sodium',
    description: 'Nutrition | Sodium | Label',
  },
  transFat: {
    defaultMessage: 'Trans Fat',
    description: 'Nutrition | Trans Fat | Label',
  },
  cholesterol: {
    defaultMessage: 'Cholesterol',
    description: 'Nutrition | Cholesterol | Label',
  },
  dietaryFiber: {
    defaultMessage: 'Dietary Fiber',
    description: 'Nutrition | Dietary Fiber | Label',
  },
  saturatedFat: {
    defaultMessage: 'Saturated Fat',
    description: 'Nutrition | Saturated Fat | Label',
  },
  sugars: {
    defaultMessage: 'Sugars',
    description: 'Nutrition | Sugars | Label',
  },
  totalFat: {
    defaultMessage: 'Total Fat',
    description: 'Nutrition | Total Fat | Label',
  },
  totalFatShort: {
    defaultMessage: 'Fat',
    description: 'Nutrition | Total Fat | Label (short)',
  },

  caloriesValue: {
    defaultMessage: '{value} cals',
    description: 'Nutrition | Calories value',
  },
  gramValue: {
    defaultMessage: '{value}g',
    description: 'Nutrition | Gram value',
  },
  milligramValue: {
    defaultMessage: '{value}mg',
    description: 'Nutrition | Milligram value',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  baseProduct: Partial<Record<Nutrition, number | null>>;
  useFallbackValues?: boolean;
};

type NutritionDataRecord = Record<Nutrition, NutritionData>;

type Nutrition =
  | 'proteinG'
  | 'totalCarbsG'
  | 'totalFatG'
  | 'calories'
  | 'caloriesFromFat'
  | 'transFatG'
  | 'sugarsG'
  | 'sodiumMg'
  | 'saturatedFatG'
  | 'cholesterolMg'
  | 'dietaryFiberG';

type NutritionData = {
  label: string;
  labelShort?: string;
  value: string | number;
} | null;
